import React from 'react';
import {
  Typography,
  Box,
  List,
  ListItem,
  Grid,
  Paper,
} from '@mui/material';
import SectionBlock from '../components/SectionBlock';
import PageLayout from '../components/PageLayout';
import CustomerReviews from '../components/CustomerReviews';
import FloatingActionButton from '../components/FloatingActionButton';
import { useTenant } from '../contexts/TenantContext';
import { useNavigate } from 'react-router-dom';
import Seo from '../components/Seo';
const ResidentialCleaningPage = () => {
  const { tenant } = useTenant();
  const navigate = useNavigate();

  return (
    <PageLayout>
      <Seo
        title="Residential House Cleaning in Orlando, FL"
        description="Recurring and one-time residential cleaning in Orlando and Central Florida: standard cleans, deep cleans, and move-in/move-out — with a checklist-driven quality guarantee."
        path="/residential-cleaning"
      />
      {/* Hero Section */}
      <SectionBlock
        bgcolor="background.paper"
        title="✨ Residential Cleaning Services in Orlando & Kissimmee"
        subtitle="Enjoy a sparkling clean home without lifting a finger. Our fairies bring the magic of pristine living to homes across Orlando and Kissimmee."
        align="center"
        py={{ xs: 6, md: 10 }}
        maxWidth="md"
      >
      </SectionBlock>

      {/* Services Included Section */}
      <SectionBlock
        bgcolor="alternate.main"
        title="🧼 What's Included"
        subtitle="Our comprehensive cleaning service covers all the essential areas of your home"
        align="left"
        py={{ xs: 4, md: 8 }}
        maxWidth="lg"
      >
        <Grid container spacing={4}>
  {/* Left column - Services */}
  <Grid item xs={12} md={8}>
    <Grid container spacing={4}>
      {[
        {
          title: 'Kitchen',
          items: [
            'Clean countertops & backsplash',
            'Wipe exterior of appliances',
            'Scrub sinks & faucets',
            'Clean stove top & microwave interior',
            'Empty trash & replace liners',
          ],
        },
        {
          title: 'Bathrooms',
          items: [
            'Disinfect toilets, tubs, & showers',
            'Wipe down mirrors & vanities',
            'Sanitize all fixtures',
            'Spot clean doors & baseboards',
          ],
        },
        {
          title: 'Living Spaces & Bedrooms',
          items: [
            'Dust furniture, shelves, & décor',
            'Vacuum carpets & mop hard floors',
            'Tidy pillows & blankets',
            'Empty trash & straighten up',
          ],
        },
        {
          title: 'Exterior Areas',
          items: [
            'Sweep patios, decks & walkways',
            'Wipe down outdoor furniture & cushions',
            'Remove cobwebs from eaves, corners & light fixtures',
            'Spot-clean exterior windowsills & door frames',
            'Sweep entry porch & tidy welcome mats',
            'Wipe railings, banisters & handrails',
            'Empty & replace liners on exterior trash/recycling bins',
          ],
        },
      ].map((section, index) => (
        <Grid item xs={12} md={6} key={index}>
          <Paper 
            elevation={3}
            sx={{ 
              p: 3, 
              bgcolor: 'background.paper', 
              borderRadius: 3,
              height: '100%',
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6
              }
            }}
          >
            <Typography variant="h6" color="primary.main" fontWeight={700} gutterBottom>
              {section.title}
            </Typography>
            <List dense>
              {section.items.map((item, idx) => (
                <ListItem key={idx} sx={{ color: 'text.secondary' }}>• {item}</ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      ))}
    </Grid>
  </Grid>

  {/* Right column - Space for content */}
  <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
    {/* Content area - booking button replaced with sticky Fab */}
    <Box sx={{ mb: 4 }}>
      {/* …your Fab or other content… */}
    </Box>

    {/* Testimonials tucked into right column */}
    <SectionBlock
      bgcolor="background.paper"
      title="💬 Client Testimonials"
      subtitle="What our happy customers say about us"
      align="center"
      py={{ xs: 4, md: 8 }}
      // remove the maxWidth so it fills the column
      sx={{ width: '100%' }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <CustomerReviews />
      </Box>
    </SectionBlock>
  </Grid>
</Grid>

      </SectionBlock>

      {/* Why Choose Us Section */}
      <SectionBlock
        bgcolor="background.paper"
        title="🧚 Why Choose 5 Fairies Cleaning?"
        subtitle="Experience the difference with our professional cleaning services"
        align="center"
        py={{ xs: 4, md: 8 }}
        maxWidth="md"
      >
        <Grid container spacing={3}>
          {[
            'Trusted Professionals – Background-checked and insured maids',
            'Easy Online Booking – 3 steps and done',
            'Flexible Scheduling – One-time, weekly, bi-weekly or monthly',
            'Transparent Pricing – No hidden fees, no surprises',
            'Local & Family-Owned – We care deeply about your satisfaction',
          ].map((item, idx) => (
            <Grid item xs={12} sm={6} key={idx}>
              <Paper 
                elevation={1}
                sx={{ 
                  p: 2, 
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  color: 'secondary.main',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                • {item}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </SectionBlock>

      {/* Add-Ons and Home Types Section */}
      <SectionBlock
        bgcolor="alternate.main"
        maxWidth="lg"
        py={{ xs: 4, md: 8 }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
              <Typography variant="h5" color="primary.main" gutterBottom>
                🌟 Optional Add-Ons
              </Typography>
              <List>
                {[
                  'Inside oven or fridge',
                  'Interior windows',
                  'Baseboard detailing',
                  'Laundry folding',
                  'Pet hair removal',
                  'Move-in / Move-out packages',
                ].map((item, idx) => (
                  <ListItem key={idx} sx={{ color: 'text.secondary' }}>• {item}</ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
              <Typography variant="h5" color="primary.main" gutterBottom>
                👨‍👩‍👧‍👦 Perfect for All Home Types
              </Typography>
              <List>
                {[
                  'Homes',
                  'Condos',
                  'Townhouses',
                  'Vacation Rentals',
                  'Airbnb Hosts',
                ].map((item, idx) => (
                  <ListItem key={idx} sx={{ color: 'text.secondary' }}>• {item}</ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      </SectionBlock>

      {/* Testimonials Section */}
      <SectionBlock
        bgcolor="background.paper"
        title="💬 Client Testimonials"
        subtitle="What our happy customers say about us"
        align="center"
        py={{ xs: 4, md: 8 }}
        maxWidth="md"
      >
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CustomerReviews />
        </Box>
      </SectionBlock>

      {/* Service Areas Section */}
      <SectionBlock
        bgcolor="alternate.main"
        title="📍 Areas We Serve"
        subtitle="Available throughout Orlando and surrounding areas"
        align="center"
        py={{ xs: 4, md: 8 }}
        maxWidth="md"
      >
        <Grid container spacing={2} justifyContent="center">
          {[
            'Orlando',
            'Kissimmee',
            'Lake Buena Vista',
            'Celebration',
            'Dr. Phillips',
            'and surrounding areas',
          ].map((item, idx) => (
            <Grid item xs={6} sm={4} key={idx}>
              <Paper 
                elevation={1}
                sx={{ 
                  p: 2, 
                  textAlign: 'center',
                  borderRadius: 2,
                  color: 'text.secondary'
                }}
              >
                {item}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </SectionBlock>

      {/* CTA Section */}
      <SectionBlock
        bgcolor="background.paper"
        title="💫 Ready to Experience the Magic?"
        subtitle="Book your clean in minutes and enjoy a spotless home"
        align="center"
        py={{ xs: 4, md: 8 }}
        maxWidth="md"
      >
        {/* Booking button replaced with sticky Fab */}
      </SectionBlock>

      {/* Guarantee and Contact Section */}
      <SectionBlock
        bgcolor="alternate.main"
        maxWidth="lg"
        py={{ xs: 4, md: 8 }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 3, height: '100%', textAlign: 'center' }}>
              <Typography variant="h5" color="primary.main" gutterBottom>
                🔒 100% Satisfaction Guarantee
              </Typography>
              <Typography color="text.secondary">
                If you're not completely satisfied, we'll come back and make it right — no questions asked.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 3, height: '100%', textAlign: 'center' }}>
              <Typography variant="h5" color="primary.main" gutterBottom>
                📞 Need More Help?
              </Typography>
              <Typography color="text.secondary" gutterBottom>
                Call or Text Us: {tenant?.clientSupportPhone || ''}
              </Typography>
              <Typography color="text.secondary">
                Email: {tenant?.contactEmail || ''}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </SectionBlock>

      {/* Floating Action Button */}
      <FloatingActionButton
        text="BOOK NOW"
        onClick={() => navigate('/booking')}
        tooltip="Book Your Clean Now!"
        position="bottom-right"
        size="auto"
        variant="text"
        ariaLabel="Book your cleaning service now"
      />
    </PageLayout>
  );
};

export default ResidentialCleaningPage;